/**
 * The 2D sheet: a PDF rendition with mark-up geometry over it.
 *
 * The PDF is rendered by pdf.js and the mark-ups live on a Konva stage sized to the
 * rendered page. Nothing is ever written back into the PDF — the file keeps the SHA-256
 * quoted on its transmittal, and the geometry is a separate record against the revision.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Arrow, Circle, Ellipse, Group, Layer, Line, Rect, Stage, Text } from 'react-konva';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Loader2, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import api from '@/lib/api';
import { cloudPoints, flat, fromPageSpace, measure, px, toPageSpace } from './markupGeometry';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const CLOSED = { rectangle: true, ellipse: true, cloud: true, highlight: true };

function Entity({ e, w, h, rotation, colour, render, onSelect, selected }) {
  const pts = px(e.points, w, h, rotation);
  const stroke = e.colour || colour;
  const common = {
    stroke,
    strokeWidth: (e.strokeWidth || 2) * (selected ? 1.8 : 1),
    opacity: render?.opacity ?? 1,
    dash: render?.dash || undefined,
    onClick: onSelect,
    onTap: onSelect,
    hitStrokeWidth: 14,
  };
  if (!pts.length) return null;
  const [[x1, y1], [x2, y2]] = [pts[0], pts[pts.length - 1]];
  switch (e.type) {
    case 'rectangle':
      return <Rect x={Math.min(x1, x2)} y={Math.min(y1, y2)} width={Math.abs(x2 - x1)}
        height={Math.abs(y2 - y1)} {...common} />;
    case 'highlight':
      return <Rect x={Math.min(x1, x2)} y={Math.min(y1, y2)} width={Math.abs(x2 - x1)}
        height={Math.abs(y2 - y1)} fill={stroke} opacity={(render?.opacity ?? 1) * 0.28}
        {...common} />;
    case 'ellipse':
      return <Ellipse x={(x1 + x2) / 2} y={(y1 + y2) / 2}
        radiusX={Math.abs(x2 - x1) / 2} radiusY={Math.abs(y2 - y1) / 2} {...common} />;
    case 'cloud':
      return <Line points={flat(cloudPoints([pts[0], pts[1]]))} closed {...common} />;
    case 'arrow':
      return <Arrow points={[x1, y1, x2, y2]} pointerLength={12} pointerWidth={10}
        fill={stroke} {...common} />;
    case 'polyline':
    case 'freehand':
      return <Line points={flat(pts)} lineCap="round" tension={e.type === 'freehand' ? 0.4 : 0}
        {...common} />;
    case 'dimension':
      return (
        <Group onClick={onSelect} onTap={onSelect}>
          <Arrow points={[x1, y1, x2, y2]} pointerAtBeginning pointerLength={9}
            pointerWidth={8} fill={stroke} {...common} />
          <Text x={(x1 + x2) / 2 + 6} y={(y1 + y2) / 2 - 16} text={e.label || ''}
            fontSize={13} fill={stroke} opacity={render?.opacity ?? 1} />
        </Group>
      );
    case 'text':
      return <Text x={x1} y={y1} text={e.text || ''} fontSize={14} fill={stroke}
        opacity={render?.opacity ?? 1} onClick={onSelect} onTap={onSelect} />;
    case 'stamp':
      return (
        <Group onClick={onSelect} onTap={onSelect} opacity={render?.opacity ?? 1}>
          <Rect x={x1} y={y1} width={Math.max(150, (e.text || '').length * 9 + 20)} height={34}
            stroke={stroke} strokeWidth={2.5} rotation={e.rotationDeg || -8} />
          <Text x={x1 + 10} y={y1 + 9} text={e.text || ''} fontSize={15} fill={stroke}
            rotation={e.rotationDeg || -8} />
        </Group>
      );
    default:
      return null;
  }
}

export default function Edm2dSheet({ fileId, markups, page, setPage, tool, colour,
  visibleLayers, onCommit, pendingText, pendingStamp, selectedId, onSelectMarkup }) {
  const [blob, setBlob] = useState(null);
  const [err, setErr] = useState(null);
  const [pages, setPages] = useState(1);
  const [scale, setScale] = useState(1.15);
  const [rotation, setRotation] = useState(0);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [pagePt, setPagePt] = useState(null);
  const [draft, setDraft] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!fileId) return;
    let dead = false;
    setBlob(null); setErr(null);
    api.get(`/edm/files/${fileId}/download`, { responseType: 'blob' })
      .then((r) => { if (!dead) setBlob(URL.createObjectURL(r.data)); })
      .catch((e) => !dead && setErr(e?.response?.data?.detail || 'Could not fetch the sheet'));
    return () => { dead = true; };
  }, [fileId]);

  const onRender = useCallback((pg) => {
    const el = wrapRef.current?.querySelector('.react-pdf__Page__canvas');
    if (el) setSize({ w: el.clientWidth, h: el.clientHeight });
    const view = pg?._pageInfo?.view || pg?.view;
    if (view) setPagePt({ width: Math.abs(view[2] - view[0]), height: Math.abs(view[3] - view[1]) });
    else if (pg?.originalWidth) setPagePt({ width: pg.originalWidth, height: pg.originalHeight });
  }, []);

  const onDown = (ev) => {
    if (!tool || !size.w) return;
    const p = ev.target.getStage().getPointerPosition();
    const [x, y] = toPageSpace(p.x / size.w, p.y / size.h, rotation);
    if (tool === 'text' || tool === 'stamp') {
      const text = tool === 'text' ? pendingText : pendingStamp;
      if (!text) return;
      onCommit({ type: tool, points: [[x, y]], text });
      return;
    }
    setDraft({ type: tool, points: [[x, y], [x, y]] });
  };

  const onMove = (ev) => {
    if (!draft || !size.w) return;
    const p = ev.target.getStage().getPointerPosition();
    const [x, y] = toPageSpace(p.x / size.w, p.y / size.h, rotation);
    setDraft((d) => (d.type === 'freehand' || d.type === 'polyline'
      ? { ...d, points: [...d.points, [x, y]] }
      : { ...d, points: [d.points[0], [x, y]] }));
  };

  const onUp = () => {
    if (!draft) return;
    const d = draft;
    setDraft(null);
    const [a, b] = [d.points[0], d.points[d.points.length - 1]];
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.004 && d.type !== 'freehand') return;
    if (d.type === 'dimension') {
      const m = measure(a, b, pagePt);
      onCommit({ ...d, label: m.label, measured: m, unitsLabel: 'mm on the sheet' },
        { measurements: m, unitsProvenance: m.unitsProvenance });
      return;
    }
    onCommit(d);
  };

  const shown = useMemo(() => (markups || []).filter(
    (m) => m.dimension === '2d'
      && (m.page === page)
      && (visibleLayers === null || visibleLayers.has(m.layerId))), [markups, page, visibleLayers]);

  if (err) {
    return <div className="p-6 text-sm text-red-400" data-testid="edm-2d-error">{err}</div>;
  }
  if (!blob) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-steel-400" data-testid="edm-2d-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Fetching the sheet…
      </div>
    );
  }

  return (
    <div data-testid="edm-2d-sheet">
      <div className="flex items-center gap-1.5 flex-wrap px-2 py-1.5 border-b"
        style={{ borderColor: 'var(--border-color)' }}>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
          data-testid="edm-2d-prev-page" className="p-1.5 rounded disabled:opacity-30 hover:bg-white/5">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs tabular-nums" data-testid="edm-2d-page-label">
          Sheet {page} of {pages}
        </span>
        <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages}
          data-testid="edm-2d-next-page" className="p-1.5 rounded disabled:opacity-30 hover:bg-white/5">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="mx-2 opacity-20">|</span>
        <button onClick={() => setScale((s) => Math.max(0.3, s - 0.2))}
          data-testid="edm-2d-zoom-out" className="p-1.5 rounded hover:bg-white/5">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs tabular-nums w-12 text-center" data-testid="edm-2d-zoom">
          {Math.round(scale * 100)}%
        </span>
        <button onClick={() => setScale((s) => Math.min(4, s + 0.2))}
          data-testid="edm-2d-zoom-in" className="p-1.5 rounded hover:bg-white/5">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button onClick={() => setRotation((r) => (r + 90) % 360)}
          data-testid="edm-2d-rotate" className="p-1.5 rounded hover:bg-white/5"
          title="Rotate the view. Mark-ups are stored in the sheet's own orientation.">
          <RotateCw className="w-4 h-4" />
        </button>
        <span className="text-[11px] ml-2" style={{ color: 'var(--text-muted)' }}
          data-testid="edm-2d-immutability">
          {pagePt ? `${Math.round(pagePt.width)}×${Math.round(pagePt.height)} pt · ` : ''}
          mark-ups are geometry, never written into the PDF
        </span>
      </div>

      <div ref={wrapRef} className="relative overflow-auto max-h-[70vh] p-4"
        style={{ background: '#1b1f24' }}>
        <div className="relative inline-block">
          <Document file={blob} onLoadSuccess={({ numPages }) => setPages(numPages)}
            loading={<div className="text-xs text-steel-400 p-6">Opening…</div>}
            error={<div className="text-xs text-red-400 p-6">pdf.js could not open this file.</div>}>
            <Page pageNumber={page} scale={scale} rotate={rotation} onRenderSuccess={onRender}
              renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
          {size.w > 0 && (
            <div className="absolute inset-0" style={{ cursor: tool ? 'crosshair' : 'default' }}>
              <Stage width={size.w} height={size.h} onMouseDown={onDown} onMouseMove={onMove}
                onMouseUp={onUp} onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}>
                <Layer>
                  {shown.map((m) => (
                    <Group key={m.comment_id}>
                      {(m.entities || []).map((e, i) => (
                        <Entity key={i} e={e} w={size.w} h={size.h} rotation={rotation}
                          colour={m.colour} render={m.render}
                          selected={selectedId === m.comment_id}
                          onSelect={() => onSelectMarkup(m.comment_id)} />
                      ))}
                      {(m.entities || []).length > 0 && (() => {
                        const p0 = px([m.entities[0].points[0]], size.w, size.h, rotation)[0];
                        return (
                          <Circle x={p0[0]} y={p0[1]} radius={9} fill={m.colour}
                            opacity={m.render?.opacity ?? 1}
                            onClick={() => onSelectMarkup(m.comment_id)} />
                        );
                      })()}
                    </Group>
                  ))}
                  {draft && (
                    <Entity e={draft} w={size.w} h={size.h} rotation={rotation} colour={colour}
                      render={{ opacity: 0.85 }} />
                  )}
                </Layer>
              </Stage>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
